<script setup>
import { computed, ref, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { trackEvent } from '@/Helpers/analytics'
import JoinGoldModal from '@/Components/Common/JoinGoldModal.vue'

/**
 * Generic "Interstitial Ad" surface — GOLD-focused for now, reused across
 * every trigger (post-purchase, star-balance click, discount unlock). Keep
 * in sync with resources/views/members3/partials/interstitial_ad_modal.blade.php
 * (the Blade twin used on non-Vue members3 pages) — same creative shape,
 * same three events.
 *
 * `creative` comes from InterstitialAdService::selectCreative() as-is:
 * {id, product_line, promo_target, headline, body, media_url, is_video,
 * media_width, media_height, cta_label, cta_sub, cta_url, cta_action,
 * min_display_seconds, legal_disclaimer, logo_url, attribution_source}. legal_disclaimer and
 * logo_url are set only for a PLUS collection creative, whose CTA starts a
 * recurring charge — the service resolves them (with the live price and the
 * real purchase URL) off that collection, so they can't be forgotten or go
 * stale against what's charged.
 * is_video (an .mp4/.webm/.mov/
 * .m4v media_url, detected server-side — see
 * InterstitialAdCreativeController::isVideoUrl()) swaps the <img> for an
 * autoplaying, muted, looping <video>. product_line === 'partners' (a
 * third-party creative, e.g. Brazzers) swaps the NA-brand stripe for an
 * ADVERTISEMENT label bar — legally required disclosure chrome, so it's
 * fixed NA-controlled markup here, never admin-editable content — and
 * swaps the CTA to a neutral (non-Gold) style, since the gold button is
 * reserved for NA GOLD CTAs specifically. cta_action === 'modal' (default
 * 'link', see InterstitialAdCtaActionEnum) opens JoinGoldModal below
 * instead of navigating to cta_url on click; the Blade twin does the same
 * via window.openJoinGoldModal. product_line === 'plus_collection' gets that
 * collection's own red CTA (never the gold treatment, and no crown), with its
 * wordmark above the button and the terms for the recurring charge below it,
 * between the CTA and the dismiss link.
 * `context` carries any extra analytics props the caller already knows
 * (e.g. purchased_product_id) so serve/click events stay joinable to the
 * moment that triggered them.
 */
const props = defineProps({
    isOpen: { type: Boolean, default: false },
    creative: { type: Object, default: null },
    trigger: { type: String, required: true },
    placement: { type: String, required: true },
    source: { type: String, required: true },
    context: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['close', 'cta'])

const page = usePage()

// Guards against a duplicate "Served" event if isOpen flips true more than
// once for the same render (e.g. a reactive re-render before the member
// closes it) — reset whenever the modal closes.
let served = false

// Timestamp the ad was actually shown, so both the CTA-click and dismiss
// handlers can report how long the member actually had it open — not just
// whether they waited out minDisplaySeconds. Reset alongside `served`.
let servedAt = null

function eventProps(extra = {}) {
    return {
        source: props.source,
        placement: props.placement,
        trigger: props.trigger,
        creative_id: props.creative?.id ?? null,
        product_line: props.creative?.product_line ?? null,
        // Which site/collection the ad was for. product_line alone collapses
        // several ads into 'sister_site'/'plus_collection', so without this a
        // report can't tell TNGF from RGN — and creative_id would need a join
        // back to a mutable admin table to find out.
        promo_target: props.creative?.promo_target ?? null,
        // So ancms's Creative Detail / Served Detail tables can render the
        // actual creative thumbnail (image or video) instead of just its id
        // — no join back to InterstitialAdCreative needed (creatives are
        // editable/rotatable, so a later admin change to media_url/is_video
        // shouldn't retroactively alter what a historical event says was
        // shown).
        media_url: props.creative?.media_url ?? null,
        is_video: props.creative?.is_video ?? false,
        user_id: page.props.auth?.user?.id ?? null,
        ...props.context,
        ...extra,
    }
}

function timeToActionMs() {
    return servedAt ? Date.now() - servedAt : null
}

// Hides the close button / "Maybe later" (replaced by a countdown, see
// remainingSeconds below) and blocks the backdrop click until
// minDisplaySeconds elapses (admin-configured, see InterstitialAdCreativeRequest);
// the CTA link is never gated by this, it's always clickable.
const canDismiss = ref(true)
const remainingSeconds = ref(0)
let dismissTimer = null
let countdownInterval = null

function stopCountdown() {
    clearTimeout(dismissTimer)
    clearInterval(countdownInterval)
}

watch(() => props.isOpen, (open) => {
    if (open && props.creative && !served) {
        served = true
        servedAt = Date.now()
        trackEvent('Interstitial Ad Served', eventProps())

        const minDisplaySeconds = props.creative.min_display_seconds ?? 0
        canDismiss.value = minDisplaySeconds <= 0
        remainingSeconds.value = minDisplaySeconds
        if (!canDismiss.value) {
            dismissTimer = setTimeout(() => {
                canDismiss.value = true
                clearInterval(countdownInterval)
            }, minDisplaySeconds * 1000)
            countdownInterval = setInterval(() => {
                remainingSeconds.value = Math.max(0, remainingSeconds.value - 1)
            }, 1000)
        }
    } else if (!open) {
        served = false
        servedAt = null
        stopCountdown()
        canDismiss.value = true
    }
    // Reset to muted every time a fresh ad opens — browsers require
    // starting muted for autoplay to be allowed at all, and carrying an
    // unmuted state over from a previous ad would be surprising.
    if (open) {
        isMuted.value = true
    }
})

// Shown while a video creative is loading (and left up indefinitely if it
// never loads at all, e.g. an unreachable/slow CDN) — without this the
// member sees a black box instead of anything branded until the first
// frame decodes. Same brand star image used as the fallback/logo
// elsewhere in this feature.
const VIDEO_POSTER_FALLBACK = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

// autoplay requires starting muted in every browser that allows it at all —
// this just lets the member opt into sound afterward. videoEl.muted is set
// directly (not just the :muted attribute) since that's the only way that
// reliably takes effect post-mount across browsers.
const isMuted = ref(true)
const videoEl = ref(null)

function toggleMute() {
    isMuted.value = !isMuted.value
    if (videoEl.value) {
        videoEl.value.muted = isMuted.value
    }
}

// Drives the ADVERTISEMENT label bar + non-Gold CTA styling below — see
// InterstitialAdProductLineEnum::isPartner() for the PHP-side twin of this
// same check.
const isPartnerAd = computed(() => props.creative?.product_line === 'partners')

// The gold button (and its crown) is reserved for NA GOLD CTAs — a collection
// unlock gets that collection's own red, matching the scene-page unlock button.
const isPlusCollectionAd = computed(() => props.creative?.product_line === 'plus_collection')

// Admin-configured per creative (InterstitialAdCtaActionEnum) — 'link' (the
// original/default behavior) navigates to cta_url on click; 'modal' opens
// ctaModalOpen below instead, with no navigation at all.
const isModalCta = computed(() => props.creative?.cta_action === 'modal')

/**
 * Whether this ad's CTA actually leads to a GOLD purchase, which is what the
 * upgrade_* cookies below claim credit for.
 *
 * True for a GOLD creative (its link goes to /gold, its modal is the Join
 * Gold Modal), and for ANY line whose cta_action is 'modal', because that
 * modal sells GOLD whatever the creative was promoting.
 *
 * False for a non-GOLD line with a link CTA — a sister site's join page, a
 * partner's own landing page. That CTA sells something else, or something
 * that isn't ours at all, so a GOLD upgrade the member happens to make in the
 * next 30 minutes is not this ad's. PLUS collection was already excluded for
 * exactly this reason; the same argument covers the rest.
 *
 * Which line drove an attributed GOLD sale stays recoverable either way:
 * interstitial_ad_creative_id is stamped unconditionally.
 */
const ctaSellsGold = computed(() => props.creative?.product_line === 'gold' || isModalCta.value)

// Independent of `isOpen`/`ctaModalOpen`'s own Teleport below (not nested
// inside the ia-overlay v-if) so it survives the parent closing this ad on
// the 'cta' emit — matches every existing call site's @cta handler, which
// treats a CTA click as "the ad's job is done, close it".
const ctaModalOpen = ref(false)

// Snapshotted in onCtaClick BEFORE emit('cta') — every existing @cta
// handler (Vault.vue/Index.vue/KebabMenu.vue) reacts to that emit by
// nulling out its own creative state, which flows back down through the
// `creative` prop reactively. Binding JoinGoldModal's :creativeId straight
// to `creative?.id` looked fine but went null by the time the member
// actually clicked "Upgrade to Gold" inside it (well after this click) —
// confirmed live via a real SegPay test transaction: psc3/psc4/psc6
// landed correctly, psc5 (creative id) was silently absent. This ref is
// what actually reaches JoinGoldModal, immune to the parent's cleanup.
const capturedCreativeId = ref(null)

function closeCtaModal() {
    ctaModalOpen.value = false
}

/**
 * Stamp the same attribution cookies MembershipUpsellModal.vue's onCtaClick
 * stamps (read back by MemberPackageReceiptHelper on the upgrade receipt
 * pages: upgrade_click_src feeds "Member Upgraded", upgrade_source feeds
 * "Trial Upgraded") so a GOLD signup driven by this ad's CTA is attributable
 * in Tinybird instead of disappearing into the un-attributed GOLD conversion
 * total. Product-level cookies reuse whatever purchase/performer context
 * this ad's own trigger already carries (post_purchase/discount_unlock) —
 * there's nothing to stamp for the star_balance_click trigger, since
 * nothing was purchased there.
 *
 * interstitial_ad_creative_id is separate from all of the above — it's
 * always stamped (not gated behind purchase context) so that when an admin
 * is rotating multiple creatives of the same product_line
 * (InterstitialAdService::eligibleCreative()), the eventual GOLD conversion
 * can be attributed back to which specific creative drove it, not just
 * lumped into the generic 'interstitial_ad' source.
 */
function onCtaClick(event) {
    // 'modal' creatives have nothing to navigate to yet (cta_url is still
    // required/stored, just unused) — stop the <a> from following it.
    if (isModalCta.value) {
        event?.preventDefault()
    }
    // Attribution cookies feed a GOLD conversion attributed back to this ad —
    // stamped regardless of cta_action. Originally this was skipped for
    // 'modal' creatives on the assumption a modal-driven purchase couldn't
    // be attributed the same way, but JoinGoldModal performs a real GOLD
    // purchase too (Lifetime or the Annual/Monthly fallback), and whichever
    // of its buttons falls through to the legacy NATS receipt-page flow
    // (MemberPackageReceiptHelper) reads these same cookies — see
    // JoinGoldModal.vue's own psc3/4/5 attribution for the more common
    // Plus-Gold-eligible path, which these cookies don't cover.
    if (window.LTIAnalytics?.setCookie) {
        const thirtyMinutesInDays = 30 / (24 * 60)
        window.LTIAnalytics.setCookie('interstitial_ad_creative_id', props.creative?.id ?? '', thirtyMinutesInDays)
        // The upgrade_* cookies specifically attribute a GOLD conversion, and
        // they're read by whatever GOLD purchase happens next within 30
        // minutes — so they're only ours to claim when this CTA is what sells
        // GOLD (see ctaSellsGold). A CTA that charges for a collection, joins
        // a sister site, or leaves for a partner would otherwise hand an
        // unrelated later GOLD upgrade to this ad, inflating its conversions
        // with sales it had nothing to do with. Those products are attributed
        // on their own path instead (psc3/psc4/psc5 on the purchase URL, see
        // InterstitialAdService::attributedPurchaseUrl).
        if (ctaSellsGold.value) {
            window.LTIAnalytics.setCookie('upgrade_click_src', 'interstitial_ad', thirtyMinutesInDays)
            window.LTIAnalytics.setCookie('upgrade_source', 'interstitial_ad', thirtyMinutesInDays)
        }
        // Same test: these say which star-shop product preceded the upgrade,
        // so they're meaningless without an upgrade this ad drove.
        if (ctaSellsGold.value && props.context?.purchased_product_id) {
            window.LTIAnalytics.setCookie('star_shop_upgrade_product_id', props.context.purchased_product_id, thirtyMinutesInDays)
            window.LTIAnalytics.setCookie('star_shop_upgrade_product_title', props.context.purchased_product_name ?? '', thirtyMinutesInDays)
            window.LTIAnalytics.setCookie('star_shop_upgrade_product_kind', props.context.purchased_product_type ?? '', thirtyMinutesInDays)
            window.LTIAnalytics.setCookie('star_shop_upgrade_product_stars', props.context.purchased_star_cost ?? '', thirtyMinutesInDays)
            if (props.context?.performer_id) {
                window.LTIAnalytics.setCookie('star_shop_upgrade_product_performer_id', props.context.performer_id, thirtyMinutesInDays)
            }
        }
    }
    trackEvent('Interstitial Ad Clicked', eventProps({ cta_label: props.creative?.cta_label ?? null, time_to_action_ms: timeToActionMs() }))
    if (isModalCta.value) {
        // Must run before emit('cta') below — see capturedCreativeId's doc.
        capturedCreativeId.value = props.creative?.id ?? null
    }
    emit('cta')
    if (isModalCta.value) {
        ctaModalOpen.value = true
    }
}

function onDismiss() {
    // Minimum display time hasn't elapsed yet — CTA stays clickable via
    // onCtaClick above, this only blocks the close/"Maybe later"/backdrop
    // paths that funnel through here.
    if (!canDismiss.value) {
        return
    }
    if (props.isOpen && props.creative) {
        trackEvent('Interstitial Ad Dismissed', eventProps({ time_to_action_ms: timeToActionMs() }))
    }
    emit('close')
}

// Modal width adapts to the creative's own image aspect ratio (from the
// auto-detected media_width/media_height — see InterstitialAdCreativeController)
// instead of one fixed size fitting every shape: a wide banner gets a wide
// modal, a square/portrait image gets a narrower one. The image itself
// already scales via width:100%/height:auto, so widening the container is
// all that's needed — it cascades to the image automatically. Clamped so
// a very wide or very tall source doesn't produce an unusable modal, and
// falls back to the CSS default (no inline style) when dimensions aren't
// known yet.
const IDEAL_IMAGE_HEIGHT = 200
const MIN_MODAL_WIDTH = 360
const MAX_MODAL_WIDTH = 640
// Landscape/square creatives (w >= h) size off the modal's own width cap
// instead of a fixed height — deriving width from IDEAL_IMAGE_HEIGHT for
// these was shrinking wide banners far below their natural size (e.g. a
// 1040x520 creative computed to 400px, noticeably small in practice) since
// a fixed 200px height times a >=1 aspect ratio stays well under a
// banner-appropriate width. Reuses MAX_MODAL_WIDTH rather than a separate
// ideal value — the largest this modal ever gets today. Portrait creatives
// (e.g. Vault Video thumbnails) keep the height-anchored formula so a tall
// narrow image doesn't blow out the modal's height instead. Keep in sync
// with interstitial_ad_modal.blade.php's applyModalWidth().
const IDEAL_LANDSCAPE_WIDTH = MAX_MODAL_WIDTH

const modalStyle = computed(() => {
    const w = props.creative?.media_width
    const h = props.creative?.media_height
    if (!w || !h) return {}

    const width = w >= h ? IDEAL_LANDSCAPE_WIDTH : Math.round(IDEAL_IMAGE_HEIGHT * (w / h))

    return { maxWidth: `${Math.min(MAX_MODAL_WIDTH, Math.max(MIN_MODAL_WIDTH, width))}px` }
})
</script>

<template>
    <Teleport to="body">
    <div v-if="isOpen && creative" class="ia-overlay">
        <div class="ia-scroll" @click.self="onDismiss">
            <div class="ia-backdrop" @click="onDismiss"></div>
            <div class="ia-content" :class="{ 'ia-content--partner': isPartnerAd }" :style="modalStyle">
                <!-- Legally-required disclosure chrome for third-party creatives — fixed
                     markup, not admin-editable, and never dependent on the partner's own
                     creative/logo to communicate that this is advertising. -->
                <div v-if="isPartnerAd" class="ia-ad-bar">
                    <span class="ia-ad-label">ADVERTISEMENT</span>
                    <button v-if="canDismiss" class="ia-ad-close" aria-label="Close" @click="onDismiss">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                    <span v-else class="ia-ad-close ia-close-countdown" aria-hidden="true">{{ remainingSeconds }}</span>
                </div>
                <template v-else>
                    <div class="ia-stripe" aria-hidden="true"></div>
                    <button v-if="canDismiss" class="ia-close" aria-label="Close" @click="onDismiss">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                    <div v-else class="ia-close ia-close-countdown" aria-hidden="true">{{ remainingSeconds }}</div>
                </template>

                <div class="ia-header">
                    <a v-if="creative.media_url" :href="isModalCta ? '#' : creative.cta_url" class="ia-image-frame" @click="onCtaClick">
                        <video
                            v-if="creative.is_video"
                            ref="videoEl"
                            :src="creative.media_url"
                            :width="creative.media_width || undefined"
                            :height="creative.media_height || undefined"
                            :poster="VIDEO_POSTER_FALLBACK"
                            class="ia-image"
                            autoplay
                            muted
                            loop
                            playsinline
                        ></video>
                        <img
                            v-else
                            :src="creative.media_url"
                            :width="creative.media_width || undefined"
                            :height="creative.media_height || undefined"
                            alt=""
                            class="ia-image"
                        >
                        <button
                            v-if="creative.is_video"
                            type="button"
                            class="ia-mute-toggle"
                            :aria-label="isMuted ? 'Unmute video' : 'Mute video'"
                            @click.stop.prevent="toggleMute"
                        >
                            <svg v-if="isMuted" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>
                        </button>
                    </a>
                    <div v-if="isPartnerAd" class="ia-partner-divider" aria-hidden="true"></div>
                    <h2 v-if="creative.headline" class="ia-title">{{ creative.headline }}</h2>
                    <p v-if="creative.body" class="ia-subtitle">{{ creative.body }}</p>
                </div>

                <!-- The collection's wordmark, so the member can see what
                     they're unlocking — same placement as the scene-page
                     unlock modal (reverse variant, this modal is dark). -->
                <img v-if="creative.logo_url" :src="creative.logo_url" class="ia-logo" alt="">
                <a v-if="creative.cta_label" :href="isModalCta ? '#' : creative.cta_url" class="ia-cta" :class="{ 'ia-cta--partner': isPartnerAd, 'ia-cta--plus': isPlusCollectionAd }" @click="onCtaClick">
                    <span class="ia-cta-row">
                        <svg v-if="!isPartnerAd && !isPlusCollectionAd" class="ia-cta-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 7l4.5 3L12 4l4.5 6L21 7l-1.8 11H4.8L3 7z"/></svg>
                        {{ creative.cta_label }}
                    </span>
                    <span v-if="creative.cta_sub" class="ia-cta-sub">{{ creative.cta_sub }}</span>
                </a>
                <!-- Server-supplied, never admin copy: a PLUS collection CTA
                     starts a recurring charge, so its terms sit between the
                     button that agrees to them and the dismiss link, where
                     they're read without pushing the CTA down the card. -->
                <p v-if="creative.legal_disclaimer" class="ia-legal">{{ creative.legal_disclaimer }}</p>
                <button v-if="canDismiss" type="button" class="ia-dismiss" @click="onDismiss">Maybe later</button>
            </div>
        </div>
    </div>
    </Teleport>

    <!-- Modal for the 'modal' CTA action (InterstitialAdCtaActionEnum) —
         reuses the same GOLD-upsell modal as the Reels pages, keyed by
         placement so its own analytics/join-URL attribution (custom8) is
         distinguishable from Reels' 'Audition_Page_Kebab_Menu'-style
         sources. Has its own JoinGoldModal-managed Teleport, independent of
         ia-overlay's v-if above, so it survives the parent closing the ad
         on the 'cta' emit. -->
    <JoinGoldModal
        :isOpen="ctaModalOpen"
        :source="creative?.attribution_source || `Interstitial_Ad_Gold_${placement}`"
        :creativeId="capturedCreativeId"
        :context="{ placement, trigger, product_line: creative?.product_line ?? null, ...context }"
        @close="closeCtaModal"
    />
</template>

<style scoped>
/* Same visual language as MembershipUpsellModal.vue's mum-* classes. */
.ia-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000020;
}
.ia-scroll {
    position: absolute;
    inset: 0;
    overflow-y: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}
.ia-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(4, 10, 24, 0.82);
    backdrop-filter: blur(3px);
}
.ia-content {
    position: relative;
    overflow: hidden;
    background: linear-gradient(165deg, #13203d 0%, #0c142b 60%, #180d18 100%);
    border: 1px solid rgba(200, 16, 46, 0.5);
    border-radius: 16px;
    padding: 0 26px 22px;
    width: 100%;
    max-width: 620px;
    text-align: center;
    color: #fff;
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.65);
}
.ia-stripe {
    height: 4px;
    margin: 0 -26px 26px;
    background: linear-gradient(90deg, #C8102E 0%, #C8102E 32%, #f4f6fb 46%, #f4f6fb 54%, #12356E 68%, #12356E 100%);
}
/* Disclosure chrome for third-party (product_line: 'partners') creatives —
   replaces .ia-stripe/.ia-close. Deliberately plain/neutral, not
   NA-branded, so it can't be mistaken for part of the partner's own
   creative. font-size is a hard floor (12px) per legal requirement —
   don't let this shrink via any inherited/relative sizing. */
.ia-content--partner .ia-header {
    padding-top: 0;
}
.ia-ad-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 0 -26px;
    padding: 10px 16px;
    background: rgba(255, 255, 255, 0.06);
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}
.ia-ad-label {
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: #dfe6f2;
}
.ia-ad-close {
    flex: none;
    width: 22px;
    height: 22px;
    padding: 3px;
    background: none;
    border: none;
    color: #8fa3c4;
    cursor: pointer;
}
.ia-ad-close:hover { color: #fff; }
.ia-partner-divider {
    height: 1px;
    margin: 0 -26px 16px;
    background: rgba(255, 255, 255, 0.15);
}
.ia-close {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 30px;
    height: 30px;
    padding: 5px;
    background: none;
    border: none;
    color: #8fa3c4;
    cursor: pointer;
}
.ia-close:hover { color: #fff; }
/* padding, not margin — margin-top here would collapse with .ia-stripe's
   margin-bottom (adjacent-sibling margin collapsing) and end up doing
   nothing, which is exactly what happened before this comment existed.
   Padding clears the close button (top:12px, 30px tall) so it never sits
   over the image below. */
.ia-header {
    padding-top: 16px;
}
/* Edge-to-edge banner at the image's own aspect ratio — no fixed
   aspect-ratio/object-fit, so nothing gets cropped or distorted regardless
   of whether the creative's image is a square icon or a wide banner
   (e.g. 1178x328). */
.ia-image-frame {
    display: block;
    position: relative;
    margin: 0 -26px 16px;
    overflow: hidden;
    background: #0c142b;
    cursor: pointer;
}
.ia-image {
    display: block;
    width: 100%;
    height: auto;
}
.ia-mute-toggle {
    position: absolute;
    right: 10px;
    bottom: 10px;
    width: 32px;
    height: 32px;
    padding: 7px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(4, 10, 24, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 50%;
    color: #fff;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s;
}
.ia-mute-toggle:hover {
    background: rgba(4, 10, 24, 0.8);
    border-color: rgba(255, 255, 255, 0.45);
}
.ia-badge {
    display: inline-block;
    background: #12356E;
    color: #dbe6ff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    padding: 4px 12px;
    border-radius: 999px;
    border: 1px solid rgba(219, 230, 255, 0.25);
    margin-bottom: 12px;
}
.ia-title {
    font-size: 24px;
    font-weight: 800;
    margin: 0 0 8px;
    color: #fff;
    letter-spacing: -0.01em;
}
.ia-subtitle {
    font-size: 14px;
    line-height: 1.55;
    color: #b6c2d9;
    margin: 0 0 18px;
}
/* Same warm-gold palette as .sbr3-goldbar in star_balance_reminder.blade.php
   (the "Go GOLD" reminder-sheet bar) instead of the Naughty red used
   elsewhere in this modal — a GOLD-product CTA reads better in gold. */
.ia-cta {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    width: 100%;
    padding: 13px 16px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), transparent 42%), linear-gradient(105deg, #4A3A16 0%, #2A2110 46%, #20180B 100%);
    border: 1px solid rgba(201, 163, 74, 0.5);
    color: #F7E4B4;
    border-radius: 12px;
    text-decoration: none;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12), 0 8px 22px -12px rgba(201, 163, 74, 0.4);
    transition: transform 0.12s, border-color 0.12s, color 0.12s;
}
.ia-cta-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: 0.01em;
}
.ia-cta-icon {
    flex: none;
    color: #D9A73E;
}
.ia-cta-sub {
    font-size: 11px;
    font-weight: 400;
    color: rgba(230, 207, 155, 0.68);
}
.ia-cta:hover {
    transform: translateY(-1px);
    border-color: rgba(201, 163, 74, 0.8);
    color: #FCEFCB;
    text-decoration: none;
}
/* Third-party partner CTA — must not use the Gold palette above, since
   that button branding is reserved for NA GOLD CTAs specifically. Neutral
   slate instead of NA red too, so it reads as "external" rather than
   another first-party CTA. Rules placed after .ia-cta/.ia-cta:hover so
   the cascade overrides them at equal specificity. */
/* PLUS collection: the collection's own unlock red (#bf0a30 / #47000f hover,
   from members-plus.css a.unlock-large) — never the gold treatment, which is
   reserved for NA GOLD CTAs. */
.ia-cta--plus {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.10), transparent 42%), linear-gradient(105deg, #bf0a30 0%, #8c0722 50%, #6d0519 100%);
    border-color: rgba(255, 255, 255, 0.28);
    color: #fff;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.16), 0 8px 22px -12px rgba(191, 10, 48, 0.55);
}
.ia-cta--plus:hover {
    border-color: rgba(255, 255, 255, 0.45);
    color: #fff;
}
.ia-cta--partner {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.06), transparent 42%), linear-gradient(105deg, #2A3442 0%, #1B222C 46%, #12161C 100%);
    border-color: rgba(255, 255, 255, 0.25);
    color: #E8EDF4;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 8px 22px -12px rgba(0, 0, 0, 0.5);
}
.ia-cta--partner:hover {
    border-color: rgba(255, 255, 255, 0.4);
    color: #fff;
}
/* Collection wordmark. Height-capped rather than width-capped: the wordmarks
   are different aspect ratios, so a fixed width would render them at wildly
   different visual sizes. */
.ia-logo {
    display: block;
    max-width: 190px;
    max-height: 46px;
    width: auto;
    height: auto;
    margin: 0 auto 10px;
    object-fit: contain;
}
/* Terms for a CTA that charges (PLUS collections). Deliberately quiet but
   readable — it has to be legible, not fine print. */
.ia-legal {
    margin: 10px auto 0;
    font-size: 12px;
    line-height: 1.45;
    color: #8fa3c4;
    text-align: center;
    max-width: 340px;
}
.ia-dismiss {
    margin-top: 8px;
    background: none;
    border: none;
    color: #8fa3c4;
    font-size: 13px;
    cursor: pointer;
}
.ia-dismiss:hover { color: #fff; }
/* Replaces the close button while minDisplaySeconds hasn't elapsed yet —
   same position/size as .ia-close/.ia-ad-close (this is applied alongside
   those classes), just centering a number instead of the X icon. */
.ia-close-countdown {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    color: #8fa3c4;
    cursor: default;
}

/* Mobile: tighter spacing (less padding wasted on a small viewport) and
   larger touch targets on the close/mute buttons (were sized for mouse
   pointers). .ia-ad-label's 12px floor is untouched here — it's a legal
   requirement at every breakpoint, not just desktop, so it's deliberately
   not in this list. */
@media (max-width: 480px) {
    .ia-scroll {
        padding: 12px;
    }
    .ia-content {
        padding: 0 18px 18px;
        border-radius: 14px;
    }
    .ia-stripe {
        margin: 0 -18px 20px;
    }
    .ia-ad-bar {
        margin: 0 -18px;
        padding: 10px 14px;
    }
    .ia-close {
        top: 8px;
        right: 8px;
        width: 36px;
        height: 36px;
        padding: 8px;
    }
    .ia-ad-close {
        width: 30px;
        height: 30px;
        padding: 5px;
    }
    /* Recomputed for the smaller .ia-close above (top:8 + height:36 = 44px
       bottom edge) so it still clears the image-frame below it — same
       ~4px buffer as desktop's 12+30=42 vs. padding-top:16 (26+16=42). */
    .ia-header {
        padding-top: 24px;
    }
    .ia-image-frame {
        margin: 0 -18px 14px;
    }
    .ia-partner-divider {
        margin: 0 -18px 14px;
    }
    .ia-title {
        font-size: 20px;
    }
    .ia-subtitle {
        font-size: 13px;
        margin: 0 0 14px;
    }
    .ia-mute-toggle {
        right: 8px;
        bottom: 8px;
        width: 36px;
        height: 36px;
        padding: 8px;
    }
    .ia-cta {
        padding: 14px;
    }
    .ia-cta-row {
        font-size: 15px;
    }
}
</style>
